/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { HistoricalChart } from 'components/HistoricalChart'
import { RegionCharts } from 'components/RegionCharts'
import { Link } from '@ta-interaktiv/contentbricks'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { browserCheck } from '@ta-interaktiv/browsercheck'

const isMobile = browserCheck.isMobile
const ARTICLE_ID = '448160073753'

export function AppComponent() {
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='ui vertical  very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>
              Die Schweiz beklagt in der zweiten Welle fast die höchste
              Übersterblichkeit in Europa
            </h1>
            <h2>
              Seit Wochen sterben in der Schweiz wegen Corona deutlich mehr
              Menschen als normalerweise in dieser Jahreszeit – trotz
              Schutzmassnahmen. In vielen anderen Ländern ist die Lage weniger
              dramatisch.
            </h2>
          </div>
        </div>
      </header>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>
              <nobr>Yannick Wiget,</nobr> <nobr>Mathias Lutz,</nobr>{' '}
              <nobr>Marc Brupbacher</nobr>
            </div>
            <div className='ta updated'>Aktualisiert am 10. Dezember 2020</div>
          </div>
          <div className='ta text'>
            <p>
              Was bleibt dereinst von dieser Pandemie? Über tägliche
              Neuinfektionen wird niemand mehr reden, wenn sich die Lage
              beruhigt hat. Aber die chronisch Kranken werden noch lange ein
              Thema sein, genauso wie die Todesopfer. An deren Anzahl wird dann
              gemessen, wie gut ein Land mit Corona fertig geworden ist. Und
              hier steht die Schweiz schlecht da.
            </p>
            <p>
              Bis jetzt sind schon über 5700 Menschen nachweislich an Covid-19
              gestorben. Pro Tag gibt es momentan etwa 76 Tote. Von vielen wird
              das{' '}
              <Link href='256173598852' allowWordWrap>
                mit einem Schulterzucken zur Kenntnis genommen
              </Link>
              . Ältere Menschen würden auch ohne die Pandemie sterben, das sei
              der Lauf der Dinge, ist ein viel gehörtes Argument. Das stimmt in
              vielen Fällen – doch es wären längst nicht so viele.
            </p>
            <p>
              Schon während der erste Welle im März und April starben in der
              Schweiz deutlich mehr über 65-Jährige als normalerweise zu dieser
              Jahreszeit. Und jetzt, im Zuge der zweiten Welle, ist die
              Übersterblichkeit noch grösser.
            </p>
          </div>
        </div>

        <div className='ui container' style={{ marginTop: '2em' }}>
          <HistoricalChart isEmbeded={true} />
        </div>
      </div>

      <div className='ui text container'>
        <div className='ta text'>
          <p>
            <Link
              href='https://www.bfs.admin.ch/bfs/de/home/statistiken/gesundheit/gesundheitszustand/sterblichkeit-todesursachen.html'
              allowWordWrap
            >
              Das Bundesamt für Statistik (BFS)
            </Link>{' '}
            zählte in der Woche vom 23. bis zum 29. November 1742 Tote in dieser
            Altersgruppe. Das waren 49 Prozent mehr als im Normalfall. In den
            drei Wochen davor gab es um die 60 Prozent mehr Tote als im
            langjährigen Schnitt. Auf dem Höhepunkt der ersten Corona-Welle lag
            dieser Wert bei 46 Prozent. Die zweite Welle verläuft also deutlich
            tödlicher.
          </p>
          <p>
            Und es sterben eben auch Jüngere: Seit Anfang November gab es bei
            den 30- bis 49-Jährigen acht Todesfälle und bei den 50- bis
            59-Jährigen sogar 30. Allerdings verzeichnet das BFS bei diesen
            Altersgruppen momentan keine Übersterblichkeit.
          </p>
          <p>
            Bei den über 65-Jährigen kommt eine solche auch unter normalen
            Bedingungen immer wieder mal vor: zum Beispiel während starken
            Grippewellen wie in den Wintern 2015 und 2017. Aber damals war die
            Übersterblichkeit viel tiefer als jetzt wegen der Corona-Pandemie –
            und das trotz all den Massnahmen, die in diesem Jahr ergriffen
            wurden, und einer Bevölkerung, die vorsichtiger agiert als in
            normalen Zeiten.
          </p>
          <div className='hint'>
            <h5>Übersterblichkeit</h5>
            <p>
              Der Begriff Übersterblichkeit bezeichnet eine Situation, in der es
              mehr Todesfälle gibt als erwartet. Diese Erwartungswerte werden
              vom BFS anhand der Fallzahlen der vorangegangenen 5 Jahre
              berechnet, unter Berücksichtigung der Veränderung der Bevölkerung.
              Es handelt sich nicht um einen exakten Wert, sondern um eine
              Bandbreite, innerhalb derer Schwankungen als zufällig gewertet
              werden. Liegen die gemeldeten Todesfallzahlen aber oberhalb dieser
              Bandbreite, dann spricht man von Übersterblichkeit. Sie betrifft
              fast ausschliesslich die Altersgruppe der über 65-Jährigen. Bei
              der jüngeren Bevölkerung gibt es auch in normalen Zeiten selten
              eine Übersterblichkeit.
            </p>
          </div>
          <p>
            Schon zum zweiten Mal in diesem Jahr erfasst die Schweiz eine
            aussergewöhnlich hohe Übersterblichkeit bei älteren Personen. Wie
            schneidet sie damit im internationalen Vergleich ab? Aufschluss
            geben Daten des Portals{' '}
            <Link href='https://www.euromomo.eu/graphs-and-maps#excess-mortality'>
              Euromomo
            </Link>
            , das vom dänischen Gesundheitsdienst für Infektionskrankheiten
            betrieben wird. Dieser arbeitet mit nationalen Behörden aus zwei
            Dutzend europäischen Ländern zusammen, darunter das Schweizer BFS.
          </p>
          <p>
            Weil die Länder grosse Bevölkerungsunterschiede aufweisen, lassen
            sie sich nicht anhand von absoluten Zahlen vergleichen. Euromomo
            berechnet deshalb einen Index, den sogenannten Z-Score, der die
            Abweichung vom Mittelwert angibt. Die Schweiz kam im November auf
            einen Z-Score von 17. Nur Belgien und Slowenien hatten während der
            zweiten Welle einen höheren Wert. Andere Länder wie Estland,
            Dänemark und Norwegen verzeichnen gar keine Übersterblichkeit.
          </p>
        </div>
      </div>

      <div className='ui container' style={{ margin: '2em 0' }}>
        <RegionCharts
          title={
            'Z-Score: Übersterblichkeit in ausgewählten Ländern, über 65-Jährige'
          }
          subtitle={'30. Dezember 2019 bis 29. November 2020'}
          source={'EuroMOMO – Z-Score'}
          regionsData={'countries.json'}
          scaleHeight={40}
          hTicks={[0, 10, 20, 30, 40, 50]}
          vTicksHidden={['Finnland']}
          annotations={{
            SI: [
              {
                text: '20,8',
                dir: 'horizontal',
                pos: isMobile ? [36, 23] : [39, 23],
                offset: [15, 0],
                align: 'middle',
                className: 'bold red'
              }
            ],
            BE: [
              {
                text: '18,6',
                dir: 'horizontal',
                pos: isMobile ? [36, 21] : [39, 21],
                offset: [15, 0],
                align: 'middle',
                className: 'bold red'
              }
            ],
            CH: [
              {
                text: '17,0',
                dir: 'horizontal',
                pos: isMobile ? [36, 20] : [39, 20],
                offset: [15, 0],
                align: 'middle',
                className: 'bold red'
              }
            ],
            IT: [
              {
                text: '15,6',
                dir: 'horizontal',
                pos: isMobile ? [36, 19] : [39, 19],
                offset: [15, 0],
                align: 'middle',
                className: 'bold red'
              }
            ],
            AT: [
              {
                text: '12,8',
                dir: 'horizontal',
                pos: isMobile ? [36, 17] : [39, 17],
                offset: [15, 0],
                align: 'middle',
                className: 'bold red'
              }
            ],
            FR: [
              {
                text: '11,5',
                dir: 'horizontal',
                pos: isMobile ? [36, 16] : [39, 16],
                offset: [15, 0],
                align: 'middle',
                className: 'bold red'
              }
            ],
            ES: [
              {
                text: '10,5',
                dir: 'horizontal',
                pos: isMobile ? [36, 14] : [39, 14],
                offset: [15, 0],
                align: 'middle',
                className: 'bold red'
              }
            ],
            NL: [
              {
                text: '8.9',
                dir: 'horizontal',
                pos: isMobile ? [36, 12] : [39, 12],
                offset: [15, 0],
                align: 'middle',
                className: 'bold red'
              }
            ],
            PT: [
              {
                text: '6,5',
                dir: 'horizontal',
                pos: isMobile ? [37, 11] : [39, 11],
                offset: [15, 0],
                align: 'middle',
                className: 'bold red'
              }
            ],
            GR: [
              {
                text: '5,9',
                dir: 'horizontal',
                pos: isMobile ? [37, 9] : [39, 9],
                offset: [15, 0],
                align: 'middle',
                className: 'bold red'
              }
            ],
            GB: [
              {
                text: '5,5',
                dir: 'horizontal',
                pos: isMobile ? [37, 9] : [39, 9],
                offset: [15, 0],
                align: 'middle',
                className: 'bold red'
              }
            ],
            SC: [
              {
                text: '5,2',
                dir: 'horizontal',
                pos: isMobile ? [37, 8.5] : [39, 8.5],
                offset: [15, 0],
                align: 'middle',
                className: 'bold red'
              }
            ],
            HU: [
              {
                text: '4,4',
                dir: 'horizontal',
                pos: isMobile ? [37, 8] : [39, 8],
                offset: [15, 0],
                align: 'middle',
                className: 'bold red'
              }
            ],
            LU: [
              {
                text: '4,0',
                dir: 'horizontal',
                pos: isMobile ? [37, 7.5] : [39, 7.5],
                offset: [15, 0],
                align: 'middle',
                className: 'bold red'
              }
            ]
          }}
        />
      </div>

      <div className='ui text container'>
        <div className='ta text'>
          <p>
            Bei der ersten Welle sah es noch anders aus. Da hatten 9 Länder eine
            höhere Übersterblichkeit als die Schweiz: Belgien, England,
            Frankreich, Irland, Italien, die Niederlanden, Schottland, Schweden
            und Spanien. Vor allem in Spanien und England starben im März und
            April viel mehr Menschen als normalerweise. Jetzt ist die Situation
            zumindest in Grossbritannien nicht mehr ganz so düster.
          </p>
          <p>
            Das ist auch in den meisten anderen Ländern so. Fast überall schlug
            die Kurve der Todesfälle während der ersten Welle höher aus als
            jetzt. Das heisst, die dortigen Behörden haben scheinbar dazugelernt
            und die Opferzahlen zu einem gewissen Grad in den Griff bekommen. Zu
            den wenigen Ausnahmen gehört die Schweiz. Hier entwickeln sich die
            Todesfallzahlen entgegen dem europäischen Trend.
          </p>
          <p>
            Innerhalb der Schweiz gibt es jedoch grosse regionale Unterschiede,
            wie BFS-Daten von 18 Kantonen zeigen. Von der ersten Welle wurden
            vor allem das Tessin und die Romandie hart getroffen. Die Kantone
            Genf und Waadt registrierten im April doppelt so viele Todesfälle
            wie normal, das Tessin sogar fast dreimal so viele. Die
            Deutschschweiz wurde im Frühling weitgehend verschont.
          </p>
        </div>
      </div>

      <div className='ui container' style={{ margin: '2em 0' }}>
        <RegionCharts
          title={
            'Wöchentliche Todesfälle in ausgewählten Kantonen, über 65-Jährige'
          }
          subtitle={'5. Januar bis 29. November 2020'}
          source={'Bundesamt für Statistik – Todesursachenstatistik'}
          regionsData={'cantons-ch.json'}
          scaleHeight={200}
          hTicks={[0, 100, 200, 300, 400, 500]}
          annotations={[]}
        />
      </div>

      <div className='ui text container'>
        <div className='ta text'>
          <p>
            Doch mit der zweiten Welle hat sich das geändert. Jetzt gibt es auch
            in der Deutschschweiz viel mehr Todesfälle. Besonders augenfällig
            ist die Übersterblichkeit in Bern, St. Gallen und Zürich. Aber auch
            Kantone mit grundsätzlich tieferen Fallzahlen beklagen auf einmal
            deutlich mehr Tote wie normalerweise. Im Thurgau beispielsweise
            sterben momentan fast doppelt so viele Menschen.
          </p>
          <p>
            Schweizweit überschreitet die Kurve nun schon die sechste Woche in
            Folge die Obergrenze. Und ein Ende ist nicht absehbar. Denn
            praktisch alle deutschschweizer Kantone verzeichnen aktuell ein
            exponentielles Wachstum bei den Corona-Neuinfektionen und{' '}
            <Link
              href='https://www.tagesanzeiger.ch/diese-kantone-fachen-die-pandemie-neu-an-999216945346'
              allowWordWrap
            >
              treiben die Pandemie wieder an
            </Link>
            . Der Anstieg der Todesfälle folgt demjenigen der Ansteckungen mit
            einer Verzögerung von etwa zwei Wochen.
          </p>
          <p>
            Wie sich die Todesfallzahlen entwickelt hätten, wenn man der
            Pandemie freien Lauf gelassen hätte, kann man nur erahnen. Aber auch
            mit den Massnahmen seitens der Behörden wird die Übersterblichkeit
            weiter zunehmen. Zu den bislang gut 5600 Corona-Opfern in der
            Schweiz (
            <Link href='https://interaktiv.tagesanzeiger.ch/2020/alle-corona-todesfaelle-in-der-schweiz/'>
              hier
            </Link>{' '}
            erinnern wir uns an möglichst viele von ihnen), werden leider noch
            viele mehr dazu kommen.
          </p>
        </div>
      </div>

      <Footer
        articleId={ARTICLE_ID}
        questions={[
          'Wie informativ war der Beitrag?',
          'Wie verständlich sind die Grafiken?'
        ]}
        credits={[
          [
            'Konzept, Recherche, Umsetzung',
            'Yannick Wiget, Marc Brupbacher, Sebastian Broschinski, Mathias Lutz'
          ],
          [
            'Daten',
            '<a href="https://www.bfs.admin.ch/bfs/de/home/statistiken/gesundheit/gesundheitszustand/sterblichkeit-todesursachen.html">Bundesamt für Statistik – Todesursachenstatistik</a>, <a href="https://www.euromomo.eu/graphs-and-maps#excess-mortality">EuroMOMO</a>'
          ]
        ]}
      ></Footer>
    </div>
  )
}
